/**
 * Pure helpers for `IpSubnetTool`. Tests live in
 * `tests/web/ipSubnetTool.test.ts`.
 *
 * IPv4-only (IPv6 is on the P1 roadmap as a separate tool). All maths
 * uses `>>> 0` to keep the underlying number unsigned 32-bit so a
 * `/0` "full internet" calc doesn't accidentally produce negative
 * broadcast addresses.
 */

export type AddrClass = 'A' | 'B' | 'C' | 'D' | 'E';

export interface SubnetInfo {
  /** Always normalised to `<network>/<prefix>`. */
  cidr: string;
  network: string;
  broadcast: string;
  mask: string;
  wildcard: string;
  /** First usable host. Equal to `network` when `hostCount === 0` (`/31` and `/32`). */
  hostMin: string;
  /** Last usable host. Equal to `broadcast` when `hostCount === 0`. */
  hostMax: string;
  /** Number of *usable* host addresses (i.e. excluding network + broadcast for prefix ≤ 30). */
  hostCount: number;
  addrClass: AddrClass;
  isPrivate: boolean;
  binary: {
    /** Prefix length, e.g. 24 for `/24`. */
    networkBits: number;
    /** 32 - networkBits. */
    hostBits: number;
    /** 32-character bit string of the network address (e.g. '11000000101010000000000100000000'). */
    binStr: string;
  };
}

const IPV4_OCTET = /^(25[0-5]|2[0-4]\d|1?\d?\d)$/;

/**
 * Parse a CIDR string (`a.b.c.d/p`) or a bare IP (treated as `/32`).
 *
 * Returns either the `SubnetInfo` snapshot, or `{ error }` so callers
 * can render the message inline. We never throw because the page has
 * the input on every keystroke and a thrown error in render would
 * blank the whole tool.
 */
export function parseCidr(input: string): SubnetInfo | { error: string } {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { error: '输入为空' };

  const slashIdx = trimmed.indexOf('/');
  const ipPart = slashIdx === -1 ? trimmed : trimmed.slice(0, slashIdx);
  const prefixPart = slashIdx === -1 ? '32' : trimmed.slice(slashIdx + 1);

  const ipNum = parseDottedQuad(ipPart);
  if (ipNum === null) return { error: 'IP 地址不合法' };

  if (!/^\d+$/.test(prefixPart)) return { error: 'CIDR 前缀必须是整数' };
  const prefix = Number(prefixPart);
  if (prefix < 0 || prefix > 32) return { error: 'CIDR 前缀必须在 0–32 之间' };

  return buildInfo(ipNum, prefix);
}

/** Parse an IP + dotted-quad mask (e.g. `192.168.1.0`, `255.255.255.0`). */
export function parseIpMask(ip: string, mask: string): SubnetInfo | { error: string } {
  const ipNum = parseDottedQuad(ip);
  if (ipNum === null) return { error: 'IP 地址不合法' };
  const maskNum = parseDottedQuad(mask);
  if (maskNum === null) return { error: '掩码不合法' };

  // Contiguous-1s check: a real IPv4 mask is N ones followed by 32-N zeros.
  // Trick: invert + 1 must be a power of two (the host part).
  const inverted = (~maskNum >>> 0) + 1;
  const isPowerOfTwo = inverted !== 0 && (inverted & (inverted - 1)) === 0;
  if (!isPowerOfTwo && maskNum !== 0xffffffff) {
    return { error: '掩码必须是连续的 1 + 0 形态' };
  }

  const prefix = popCount(maskNum);
  return buildInfo(ipNum, prefix);
}

function buildInfo(ipNum: number, prefix: number): SubnetInfo {
  // `>>> 0` coerces to unsigned 32-bit so we never produce negative
  // results for high-prefix shifts.
  const maskNum =
    prefix === 0 ? 0 : prefix === 32 ? 0xffffffff : (~((1 << (32 - prefix)) - 1) >>> 0);
  const networkNum = (ipNum & maskNum) >>> 0;
  const wildcardNum = (~maskNum >>> 0);
  const broadcastNum = (networkNum | wildcardNum) >>> 0;

  let hostMin: number;
  let hostMax: number;
  let hostCount: number;
  if (prefix >= 31) {
    // /31: RFC 3021 point-to-point — 2 addresses, both "usable" by the
    //      hosts at each end. /32: a single address.
    // We keep this simple and report `hostCount` as 2 / 1 to match
    // what subnet-calculator.com etc. show; the network/broadcast
    // distinction collapses for these prefixes.
    hostMin = networkNum;
    hostMax = broadcastNum;
    hostCount = prefix === 32 ? 1 : 2;
  } else {
    hostMin = (networkNum + 1) >>> 0;
    hostMax = (broadcastNum - 1) >>> 0;
    hostCount = (broadcastNum - networkNum - 1);
  }

  const networkBin = networkNum.toString(2).padStart(32, '0');

  return {
    cidr: `${formatDottedQuad(networkNum)}/${prefix}`,
    network: formatDottedQuad(networkNum),
    broadcast: formatDottedQuad(broadcastNum),
    mask: formatDottedQuad(maskNum),
    wildcard: formatDottedQuad(wildcardNum),
    hostMin: formatDottedQuad(hostMin),
    hostMax: formatDottedQuad(hostMax),
    hostCount,
    addrClass: classify(ipNum),
    isPrivate: isPrivate(ipNum),
    binary: {
      networkBits: prefix,
      hostBits: 32 - prefix,
      binStr: networkBin,
    },
  };
}

function parseDottedQuad(s: string): number | null {
  if (!s) return null;
  const parts = s.split('.');
  if (parts.length !== 4) return null;
  let n = 0;
  for (const p of parts) {
    if (!IPV4_OCTET.test(p)) return null;
    n = (n << 8) | Number(p);
  }
  return n >>> 0;
}

function formatDottedQuad(n: number): string {
  return [
    (n >>> 24) & 0xff,
    (n >>> 16) & 0xff,
    (n >>> 8) & 0xff,
    n & 0xff,
  ].join('.');
}

function popCount(n: number): number {
  let count = 0;
  let x = n >>> 0;
  while (x) {
    count += x & 1;
    x >>>= 1;
  }
  return count;
}

function classify(ipNum: number): AddrClass {
  // First octet ranges per RFC 791 + IANA classful (legacy but still
  // a useful "what kind of address am I looking at" hint).
  const a = (ipNum >>> 24) & 0xff;
  if (a < 128) return 'A';
  if (a < 192) return 'B';
  if (a < 224) return 'C';
  if (a < 240) return 'D';
  return 'E';
}

function isPrivate(ipNum: number): boolean {
  // RFC 1918 + RFC 6598 (carrier-grade NAT) + RFC 3927 (link-local) +
  // loopback. We treat all four as "private-ish" because that's how
  // most users mean the question.
  const a = (ipNum >>> 24) & 0xff;
  const b = (ipNum >>> 16) & 0xff;
  if (a === 10) return true; // 10.0.0.0/8
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
  if (a === 192 && b === 168) return true; // 192.168.0.0/16
  if (a === 100 && b >= 64 && b <= 127) return true; // 100.64.0.0/10 (CGNAT)
  if (a === 169 && b === 254) return true; // 169.254.0.0/16 (link-local)
  if (a === 127) return true; // loopback
  return false;
}
